import { formatMoney } from './novicell_shopify-utility.js';

// Swatch maps are cached per product handle; stock status is always re-fetched (inventory changes).
const productSwatchMapCache = new Map();

export function initializeUpsellDrawerOptionEvents(product, moneyFormat) {
  const optionSections = document.querySelectorAll('.upsell-drawer-material-selects-switch');

  optionSections.forEach(section => {
    const clickableElems = section.querySelectorAll('label, img');
    const valueSpan = section.querySelector('.seleceted-option-value');

    if (clickableElems.length > 0) {
      const firstElem = clickableElems[0];
      firstElem.classList.add('active');
      if (valueSpan) {
        valueSpan.textContent = firstElem.getAttribute('data-option-value-name');
      }
    }

    clickableElems.forEach(elem => {
      // <label> without `for` and <img> aren't focusable by default and
      // don't dispatch click on Enter/Space. Promote them so keyboard users
      // can reach and activate every variant option.
      if (!elem.hasAttribute('tabindex')) elem.setAttribute('tabindex', '0');
      if (!elem.hasAttribute('role')) elem.setAttribute('role', 'button');
      if (!elem.hasAttribute('aria-label')) {
        const name = elem.getAttribute('data-option-value-name') || elem.alt;
        if (name) elem.setAttribute('aria-label', name);
      }
      elem.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          elem.click();
        }
      });

      elem.addEventListener('click', () => {
        clickableElems.forEach(i => i.classList.remove('active'));
        elem.classList.add('active');

        if (valueSpan) {
          valueSpan.textContent = elem.getAttribute('data-option-value-name');
        }

        const selectedOptions_ = [];
        optionSections.forEach(sec => {
          const span = sec.querySelector('.seleceted-option-value');
          if (span) {
            const value = span.textContent.trim();
            if (value) selectedOptions_.push(value);
          }
        });

        const matchedVariant = product.variants.find(variant => {
          return selectedOptions_.every((opt, index) => {
            return opt === variant[`option${index + 1}`];
          });
        });

        const priceLoweredElem = document.querySelector('.upsell-drawer-price-lowered');
        const priceRegularElem = document.querySelector('.upsell-drawer-price-regular');

        if (priceLoweredElem) {
          priceLoweredElem.textContent = formatMoney(matchedVariant.price, moneyFormat).replace(/,/g, '.');
        }

        if (priceRegularElem) {
          if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
            priceRegularElem.textContent = formatMoney(matchedVariant.compare_at_price, moneyFormat).replace(/,/g, '.');
          } else {
            priceRegularElem.textContent = '';
          }
        }

        const priceSpan = document.querySelector('#upsell-drawer-add-to-cart span');
        if (priceSpan) {
          priceSpan.textContent = formatMoney(matchedVariant.price, moneyFormat);
        }

        document.getElementById('upsell-drawer-add-to-cart').setAttribute('data-product-id', matchedVariant.id);

        const selectedOptions = [];
        optionSections.forEach(sec => {
          const span = sec.querySelector('.seleceted-option-value');
          if (span) {
            const value = span.textContent.trim();
            if (value) selectedOptions.push(value);
          }
        });

        const galleryImages = document.querySelectorAll('.scroll-container img');
        let matchedImage = null;

        galleryImages.forEach(galleryImg => {
          const opt1 = galleryImg.getAttribute('option1') || '';
          const opt2 = galleryImg.getAttribute('option2') || '';
          const opt3 = galleryImg.getAttribute('option3') || '';
          const options = [opt1, opt2, opt3];

          const isMatch = selectedOptions.every(opt => options.includes(opt));
          if (isMatch) {
            matchedImage = galleryImg;
          }
        });

        if (matchedImage) {
          matchedImage.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }

        const url = `${window.location.origin}/products/${product.handle}?variant=${matchedVariant.id}`;

        fetch(url)
          .then(response => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then(htmlString => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlString, 'text/html');
            const stockStatus = doc.querySelector('stock-status');

            if (stockStatus) {
              const deliveryEl = document.querySelector('.upsell-drawer-delivery');
              if (deliveryEl) {
                deliveryEl.innerHTML = stockStatus.innerHTML;
              }
            }
          })
          .catch(error => {
            console.error('Error:', error);
          });
      });
    });
  });
}

export async function populateUpsellDrawer(product, moneyFormat) {
  function escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // UPSELL DRAWER GALLERY
  const imagesHtml = product.images.map(image => {
    let option1 = "";
    let option2 = "";
    let option3 = "";

    for (const variant of product.variants) {
      if (variant.featured_image != null) {
        variant.featured_image.src = variant.featured_image.src.replace(/^https:/, '');
      }

      const imgSource = typeof image === 'string' ? image : image.src;

      if (variant.featured_image != null && variant.featured_image.src === imgSource) {
        option1 = variant.option1 || "";
        option2 = variant.option2 || "";
        option3 = variant.option3 || "";
        break;
      }
    }

    const imgSrc = typeof image === 'string' ? image : image.src;
    return `<img src="${imgSrc}&width=308" option1="${escapeHTML(option1)}" option2="${escapeHTML(option2)}" option3="${escapeHTML(option3)}" alt="">`;
  }).join("");

  const container = document.querySelector('.upsell-gallery-body');
  if (container) {
    container.innerHTML = imagesHtml;
  }

  // UPSELL DRAWER TITLE
  const primaryTitleElem = document.querySelector('.upsell-primary-title');
  const secondaryTitleElem = document.querySelector('.upsell-secondary-title');

  if (product.title) {
    // No manual escaping needed here as we use .textContent below, 
    // which handles escaping automatically and prevents XSS.
    const parts = product.title.split(',');
    const primary = parts[0].trim();
    const secondary = parts.length > 1 ? parts.slice(1).join(',').trim() : "";

    if (primaryTitleElem) primaryTitleElem.textContent = primary;
    if (secondaryTitleElem) secondaryTitleElem.textContent = secondary;
  }

  // UPSELL DRAWER PRICES
  const priceLoweredElem = document.querySelector('.upsell-drawer-price-lowered');
  const priceRegularElem = document.querySelector('.upsell-drawer-price-regular');

  if (priceLoweredElem) {
    priceLoweredElem.textContent = formatMoney(product.price, moneyFormat).replace(/,/g, '.');
  }

  if (priceRegularElem) {
    if (product.compare_at_price && product.compare_at_price > product.price) {
      priceRegularElem.textContent = formatMoney(product.compare_at_price, moneyFormat).replace(/,/g, '.');
    } else {
      priceRegularElem.textContent = '';
    }
  }

  // UPSELL DRAWER VENDOR
  const vendorSpan = document.querySelector('.upsell-drawer-designer span.upsell-drawer-designer-span');
  if (vendorSpan) {
    vendorSpan.textContent = product.vendor || '';
  }

  // UPSELL DRAWER DESCRIPTION
  const descriptionWrapper = document.querySelector('.upsell-drawer-product-description');
  if (descriptionWrapper) {
    const linkElement = descriptionWrapper.querySelector('#see-full-product');
    if (linkElement) {
      const oldDesc = descriptionWrapper.querySelector('.js-upsell-description');
      if (oldDesc) oldDesc.remove();

      const descEl = document.createElement('div');
      descEl.className = 'js-upsell-description';
      descEl.innerHTML = product.description || '';
      descriptionWrapper.insertBefore(descEl, linkElement);
      linkElement.setAttribute('href', '/products/' + product.handle);
    }
  }

  // UPSELL DRAWER BUTTON PRICE
  const priceSpan = document.querySelector('#upsell-drawer-add-to-cart span');
  if (priceSpan) {
    priceSpan.textContent = formatMoney(product.price, moneyFormat);
  }

  const addToCartBtn2 = document.getElementById("upsell-drawer-add-to-cart")
  if (addToCartBtn2) {
    if (product.variants && product.variants.length > 0) {
      const variantId = product.variants[0].id
      addToCartBtn2.setAttribute("data-product-id", variantId)
      addToCartBtn2.style.display = "flex"
    } else {
      addToCartBtn2.setAttribute("data-product-id", "")
      addToCartBtn2.style.display = "none"
    }
  }

  // Fetch the product page once to get both swatch URLs (with correct casing from Liquid) and
  // stock status. Swatch maps are cached per handle; stock status is always fresh.
  const productPageUrl = `${window.location.origin}/products/${product.handle}`;
  try {
    const response = await fetch(productPageUrl);
    if (response.ok) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(await response.text(), 'text/html');

      if (!productSwatchMapCache.has(product.handle)) {
        const map = {};
        // .variant__color-swatch-label[data-tooltip] is exactly what Liquid renders in
        // novicell_product-variant-options.liquid when show_as_swatch is true.
        doc.querySelectorAll('.variant__color-swatch-label[data-tooltip]').forEach(label => {
          const name = label.getAttribute('data-tooltip');
          const urlMatch = (label.getAttribute('style') || '').match(/url\(['"]?([^'"?]+)/);
          if (name && urlMatch) map[name] = urlMatch[1];
        });
        productSwatchMapCache.set(product.handle, map);
      }

      const stockStatus = doc.querySelector('stock-status');
      if (stockStatus) {
        const deliveryEl = document.querySelector('.upsell-drawer-delivery');
        if (deliveryEl) deliveryEl.innerHTML = stockStatus.innerHTML;
      }
    }
  } catch {
    // Proceed without swatches/stock on fetch failure
  }

  const swatchMap = productSwatchMapCache.get(product.handle) ?? {};

  // OPTIONS
  const optionsWrapper = document.querySelector('.upsell-drawer-material-selects');
  if (optionsWrapper && product.options) {
    optionsWrapper.innerHTML = '';

    const template = document.createElement('template');
    template.innerHTML = `
    <div class="upsell-drawer-material-selects-switch tw-font-secondary tw-mr-16">
      <p class="tw-mb-3">
        <span class="option-name"></span>:<span class="seleceted-option-value tw-font-normal tw-font-primary tw-leading-normal"></span>
      </p>
      <div class="upsell-drawer-material-selects-options tw-flex tw-flex-wrap"></div>
    </div>
    `;

    product.options.forEach(option => {
      const switchDiv = template.content.cloneNode(true);
      switchDiv.querySelector('.option-name').textContent = option.name;
      const optionsContainer = switchDiv.querySelector('.upsell-drawer-material-selects-options');

      option.values.forEach(value => {
        const swatchUrl = swatchMap[value];
        if (swatchUrl) {
          const img = document.createElement('img');
          img.className = 'tw-object-fill tw-cursor-pointer tw-border-0 tw-border-transparent tw-p-0 tw-transition-colors tw-duration-200 tw-ease-in-out tw-mr-3 tw-mb-3';
          img.alt = value;
          img.setAttribute('data-option-value-name', value);
          img.src = swatchUrl;
          optionsContainer.appendChild(img);
        } else {
          const label = document.createElement('label');
          label.textContent = value;
          label.setAttribute('data-option-value-name', value);
          label.setAttribute('class', 'tw-mr-3 tw-mb-3 tw-text-sm tw-text-center tw-cursor-pointer');
          optionsContainer.appendChild(label);
        }
      });

      optionsWrapper.appendChild(switchDiv);
    });
  }

  initializeUpsellDrawerOptionEvents(product, moneyFormat);
}

const UPSELL_DRAWER_FOCUSABLE_SELECTOR = [
  '.close-btn',
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function openUpsellDrawer() {
  const drawer = document.querySelector('.drawer-upsell');
  if (!drawer) return;
  drawer.classList.add('active');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.style.overflow = 'hidden';

  // Move keyboard focus into the drawer once it has finished animating in.
  // The .drawer__inner slides via `transform`, so calling .focus() before
  // the panel reaches the viewport can silently no-op in some browsers.
  // Listen for transitionend; fall back to setTimeout in case the
  // transition is disabled (reduced motion, etc.) and never fires.
  let focused = false;
  const moveFocus = () => {
    if (focused) return;
    focused = true;
    drawer.removeEventListener('transitionend', onTransitionEnd);
    const firstFocusable = drawer.querySelector(UPSELL_DRAWER_FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  };
  const onTransitionEnd = (event) => {
    // Only react to the panel's own transform/visibility transitions,
    // not transitions on bubbled-up children.
    if (event.target !== drawer && !event.target.classList.contains('drawer__inner')) return;
    moveFocus();
  };
  drawer.addEventListener('transitionend', onTransitionEnd);
  setTimeout(moveFocus, 400);
}

// Add to cart handler — registered once here so it works whether the page
// loads novicell_upsell.js, novicell_cart-upsell.js, or both.
document.addEventListener("click", (event) => {
  const button = event.target.closest(".upsell-add-item-to-cart");
  if (!button) return;
  event.preventDefault();
  if (button.disabled) return;
  button.disabled = true;
  const variantId = button.getAttribute("data-product-id");
  if (!variantId) {
    button.disabled = false;
    document.getElementById('upsell-drawer')?.classList.remove('active');
    return;
  }
  (async () => {
    try {
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: Number.parseInt(variantId, 10), quantity: 1 }),
      });
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(`Error adding product: ${errorData.description || addResponse.statusText}`);
      }
      await addResponse.json();
      await refreshCartDrawerHTML();
      await updateCartIconBubble();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      button.disabled = false;
      document.getElementById('upsell-drawer')?.classList.remove('active');
    }
  })();
});

async function refreshCartDrawerHTML() {
  try {
    const sectionsResponse = await fetch(`/?section_id=cart-drawer&timestamp=${Date.now()}`, {
      headers: { Accept: "text/html", "Cache-Control": "no-cache" },
    });
    if (!sectionsResponse.ok) throw new Error(`Sections fetch error: ${sectionsResponse.status}`);
    const newHTML = await sectionsResponse.text();
    const currentCartDrawer = document.querySelector("cart-drawer");
    if (!currentCartDrawer) return;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = newHTML;
    const newCartDrawer = tempDiv.querySelector("cart-drawer");
    if (!newCartDrawer) return;
    currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
    Array.from(newCartDrawer.attributes).forEach((attr) => {
      if (attr.name !== "id") currentCartDrawer.setAttribute(attr.name, attr.value);
    });
    if (typeof currentCartDrawer.open === "function") currentCartDrawer.open();
    else {
      currentCartDrawer.classList.add("active");
      currentCartDrawer.setAttribute("open", "");
    }
    await updateCartIconBubble();
  } catch {
    await fallbackSectionsUpdate();
  }
}

async function fallbackSectionsUpdate() {
  try {
    const sectionsResponse = await fetch(`/?sections=cart-drawer,cart-icon-bubble&timestamp=${Date.now()}`, {
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    });
    if (!sectionsResponse.ok) throw new Error(`Fallback fetch error: ${sectionsResponse.status}`);
    const sectionsData = await sectionsResponse.json();
    if (sectionsData["cart-drawer"]) {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.innerHTML = sectionsData["cart-drawer"];
        if (typeof cartDrawer.open === "function") cartDrawer.open();
        else {
          cartDrawer.classList.add("active");
          cartDrawer.setAttribute("open", "");
        }
      }
    }
    if (sectionsData["cart-icon-bubble"]) {
      const bubble = document.getElementById("cart-icon-bubble");
      const current = bubble && bubble.querySelector("[data-cart-count-bubble]");
      if (current) {
        const next = new DOMParser()
          .parseFromString(sectionsData["cart-icon-bubble"], "text/html")
          .querySelector("[data-cart-count-bubble]");
        if (next) current.innerHTML = next.innerHTML;
      }
    }
  } catch {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      if (typeof cartDrawer.open === "function") cartDrawer.open();
      else {
        cartDrawer.classList.add("active");
        cartDrawer.setAttribute("open", "");
      }
    }
  }
}

async function updateCartIconBubble() {
  try {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const cartResponse = await fetch("/cart.js?" + Date.now());
    if (cartResponse.ok) {
      const cartData = await cartResponse.json();
      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (cartIconBubble) {
        let countDiv = cartIconBubble.querySelector("div > span[aria-hidden]");
        if (cartData.item_count > 0) {
          if (!countDiv) {
            countDiv = document.createElement("div");
            countDiv.className = "max-[990px]:tw-self-end";
            cartIconBubble.appendChild(countDiv);
          }
          countDiv.innerHTML = `<span aria-hidden="true">(${cartData.item_count})</span><span class="visually-hidden">(${cartData.item_count} items)</span>`;
        } else {
          if (countDiv) countDiv.remove();
        }
      }
    }
  } catch (error) {
    console.error("Error updating cart count:", error);
  }
}
